import { defaults } from 'chart.js';

export function normalizeChartOptions(options = {}) {
  const normalized = {
    ...options,
  };

  const plugins = {
    ...(normalized.plugins || {}),
  };

  if (normalized.legend) {
    plugins.legend = {
      ...(plugins.legend || {}),
      ...normalized.legend,
    };
    delete normalized.legend;
  }

  if (normalized.title) {
    const title = normalized.title || {};
    plugins.title = {
      ...(plugins.title || {}),
      ...title,
      font: {
        ...(title.font || {}),
        ...(title.fontSize ? { size: title.fontSize } : {}),
      },
    };
    delete normalized.title;
  }

  if (normalized.tooltips) {
    const tooltips = normalized.tooltips || {};
    const callbacks = {
      ...(tooltips.callbacks || {}),
    };

    if (typeof callbacks.label === 'function') {
      const legacyLabel = callbacks.label;
      callbacks.label = (context) => legacyLabel(
        {
          datasetIndex: context.datasetIndex,
          dataIndex: context.dataIndex,
          index: context.dataIndex,
          yLabel: context.parsed?.y ?? context.parsed?.x,
          xLabel: context.parsed?.x ?? context.parsed?.y,
        },
        {
          datasets: context.chart?.data?.datasets || [],
          labels: context.chart?.data?.labels || [],
        }
      );
    }

    plugins.tooltip = {
      ...(plugins.tooltip || {}),
      ...tooltips,
      callbacks,
    };
    delete normalized.tooltips;
  }

  normalized.plugins = plugins;

  const legacyScales = normalized.scales || {};
  const legacyXAxes = Array.isArray(legacyScales.xAxes) ? legacyScales.xAxes[0] : null;
  const legacyYAxes = Array.isArray(legacyScales.yAxes) ? legacyScales.yAxes[0] : null;
  const legacyScale = normalized.scale || null;

  if (legacyXAxes || legacyYAxes || legacyScale) {
    const convertAxis = (axis) => {
      if (!axis) {
        return null;
      }

      const nextAxis = { ...axis };

      if (nextAxis.scaleLabel) {
        nextAxis.title = {
          ...(nextAxis.title || {}),
          display: nextAxis.scaleLabel.display ?? true,
          text: nextAxis.scaleLabel.labelString || nextAxis.title?.text || '',
        };
        delete nextAxis.scaleLabel;
      }

      /*
       * Chart.js 2 trug die Achsengrenzen unter `ticks`, Chart.js 3 hat sie
       * eine Ebene höher an die Achse selbst geholt. Nachgelesen in
       * `node_modules/chart.js/dist/chart.js`: gelesen werden
       * `options.suggestedMax` (3833) und `options.beginAtZero` (3906, 10262,
       * 10482) — unter `ticks` schaut dort niemand mehr nach.
       *
       * Ohne diese Umhebung verliert die VALERI-Seite wortlos die Nullbasis
       * des Netzdiagramms und den Nullpunkt der Amortisationsachse.
       */
      if (nextAxis.ticks) {
        const ticks = { ...nextAxis.ticks };
        for (const schluessel of ['min', 'max', 'suggestedMin', 'suggestedMax', 'beginAtZero', 'reverse']) {
          if (schluessel in ticks) {
            if (!(schluessel in nextAxis)) {
              nextAxis[schluessel] = ticks[schluessel];
            }
            delete ticks[schluessel];
          }
        }
        nextAxis.ticks = ticks;
      }

      if (nextAxis.gridLines) {
        const { zeroLineColor, zeroLineWidth, ...gridLines } = nextAxis.gridLines;
        nextAxis.grid = {
          ...(nextAxis.grid || {}),
          ...gridLines,
        };

        /*
         * `zeroLineColor`/`zeroLineWidth` gibt es in Chart.js 4 nicht mehr —
         * die Zeichenfolge „zeroLine" kommt im ganzen Build nicht mehr vor.
         * Der Ersatz ist ein auswertbares `grid.color`/`grid.lineWidth`, das
         * den Tick bei 0 gesondert behandelt. Für die Kapitalwertkurve ist
         * das die Gewinnschwelle, also die wichtigste Linie des Diagramms.
         */
        if (zeroLineColor !== undefined || zeroLineWidth !== undefined) {
          const grundfarbe = nextAxis.grid.color;
          const grundbreite = nextAxis.grid.lineWidth;
          if (zeroLineColor !== undefined) {
            nextAxis.grid.color = (ctx) => (ctx.tick?.value === 0
              ? zeroLineColor
              : (typeof grundfarbe === 'function' ? grundfarbe(ctx) : grundfarbe) ?? defaults.borderColor);
          }
          if (zeroLineWidth !== undefined) {
            nextAxis.grid.lineWidth = (ctx) => (ctx.tick?.value === 0
              ? zeroLineWidth
              : (typeof grundbreite === 'function' ? grundbreite(ctx) : grundbreite) ?? 1);
          }
        }

        delete nextAxis.gridLines;
      }

      return nextAxis;
    };

    const nextScales = {
      ...(Object.fromEntries(Object.entries(legacyScales).filter(([key]) => !['xAxes', 'yAxes'].includes(key)))),
    };

    const xAxis = convertAxis(legacyXAxes);
    const yAxis = convertAxis(legacyYAxes);
    const radialAxis = convertAxis(legacyScale);

    if (xAxis) {
      nextScales.x = xAxis;
    }
    if (yAxis) {
      nextScales.y = yAxis;
    }
    if (radialAxis) {
      nextScales.r = radialAxis;
    }

    normalized.scales = nextScales;
    delete normalized.scale;
  }

  if (normalized.scales?.xAxes) {
    delete normalized.scales.xAxes;
  }
  if (normalized.scales?.yAxes) {
    delete normalized.scales.yAxes;
  }

  return normalized;
}
